import { useLayoutEffect, useState } from 'react';

/** Ниже этого список не сжимаем: две строки и намёк на прокрутку. */
const MENU_MIN_HEIGHT = 96;
/** Зазор между низом списка и краем окна, внутри которого он лежит. */
const MENU_EDGE_GAP = 12;

/** Ближайший предок, который сам прокручивается (панель модалки): он же и обрезает выпавший за край список. */
function findScrollContainer(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);

    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }

  return null;
}

/**
 * Высота выдачи, которая помещается в окно, где лежит список.
 *
 * Меню абсолютное и лежит поверх прокручиваемой панели (модалка — `overflow: auto`): список выше
 * остатка панели вылезал за её низ, последняя строка оказывалась обрезана. Поэтому высота ограничивается
 * местом до низа ближайшей прокручиваемой панели (или окна браузера), но не выше `maxHeight` и не ниже
 * двух строк. Вне прокручиваемой панели возвращается `maxHeight` как есть. Пересчёт — при смене размера окна.
 */
export function useFitMenuHeight(listRef: { current: HTMLElement | null }, maxHeight: number): number {
  const [height, setHeight] = useState(maxHeight);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;

    const fit = () => {
      const container = findScrollContainer(list);
      if (!container) {
        setHeight(maxHeight);
        return;
      }

      const bottomEdge = Math.min(container.getBoundingClientRect().bottom, window.innerHeight);
      const free = Math.floor(bottomEdge - list.getBoundingClientRect().top - MENU_EDGE_GAP);

      setHeight(Math.max(MENU_MIN_HEIGHT, Math.min(maxHeight, free)));
    };

    fit();
    window.addEventListener('resize', fit);

    return () => window.removeEventListener('resize', fit);
  }, [listRef, maxHeight]);

  return height;
}
